import React, { useMemo, useState, useRef, useCallback } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
  TooltipItem,
  ActiveElement,
  ChartEvent,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';

import { useMaterialsStore } from '@components/Stores/MaterialsStore';
import { GENERAL_ID } from '@components/Stores/exposureDataStore';

import { ToggleChips } from '../../Dashboard/dashboard-components/ToggleChips';
import { AttachMoney, Inventory } from '@mui/icons-material';
import { IExposureHistory } from '../../../utils/exposureUtils';
import { IRiskTolerance } from '../../../sp-types/accountTypes';
import { useCurrencyRates } from '../../../hooks/useCurrencyRates';
import { FormatToShortPriceTemplate } from '../../../utils/FormatValue';
import { getExposureStatus, getExposureColor } from '../../../utils/RiskUtils';

ChartJS.register(CategoryScale, TimeScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

type RangeKey = '1Y' | '2Y';
const RANGE_OPTIONS: { label: string; value: RangeKey }[] = [
  { label: '1 Year', value: '1Y' },
  { label: '2 Years', value: '2Y' },
];

const EXPOSURE_COLOR = '#2563EB';

interface Props {
  exposureData?: IExposureHistory[];
  exposureThresholds?: { dangerLow: number; warnLow: number; warnHigh: number; dangerHigh: number };
  formulaId: string;
  selectedSnapshot?: IExposureHistory | null;
  onPointClick?: (snapshot: IExposureHistory | null) => void;
}
export const ExposureChart: React.FC<Props> = ({ exposureData, exposureThresholds, formulaId, selectedSnapshot, onPointClick }) => {
  const [selectedRangeIndex, setSelectedRangeIndex] = useState(0);
  const [isQuantity, setIsQuantity] = useState(false);
  const activeRange = useMemo(() => RANGE_OPTIONS[selectedRangeIndex]?.value ?? '1Y', [selectedRangeIndex]);
  const { materialsMap } = useMaterialsStore();
  const { pricesMap } = useCurrencyRates();
  const isGeneral = formulaId === GENERAL_ID;
  const chartRef = useRef<any>(null);

  const price = isGeneral ? 1 : (pricesMap[formulaId] || 1);
  const isQty = isQuantity && !isGeneral;

  // Derive selectedIndex from selectedSnapshot within filteredData
  // (computed after filteredData is defined, but declared here for clarity)

  // Sort and filter exposure data from API by selected range
  const sortedData = useMemo(() => {
    if (!exposureData?.length) return [];
    return exposureData
      .slice()
      .sort((a, b) => new Date(a.createDate).getTime() - new Date(b.createDate).getTime());
  }, [exposureData]);

  const filteredData = useMemo(() => {
    if (!sortedData.length) return [];
    if (activeRange === '1Y') {
      const cutoff = new Date();
      cutoff.setFullYear(cutoff.getFullYear() - 1);
      return sortedData.filter(d => new Date(d.createDate) >= cutoff);
    }
    return sortedData;
  }, [sortedData, activeRange]);

  const selectedIndex = useMemo(() => {
    if (!selectedSnapshot || !filteredData.length) return -1;
    return filteredData.findIndex(d => d.createDate === selectedSnapshot.createDate);
  }, [selectedSnapshot, filteredData]);

  // Threshold lines from watch-rule config
  const thresholdLines = useMemo(() => {
    if (!exposureThresholds) return [];
    const t = exposureThresholds;
    const div = isQty ? price : 1;
    return [
      { label: 'Danger', value: t.dangerLow / div, color: '#EF4444', skipLegend: false },
      { label: 'Warning', value: t.warnLow / div, color: '#F59E0B', skipLegend: false },
      { label: 'Warning ', value: t.warnHigh / div, color: '#F59E0B', skipLegend: true },
      { label: 'Danger ', value: t.dangerHigh / div, color: '#EF4444', skipLegend: true },
    ];
  }, [exposureThresholds, isQty, price]);

  // Build IRiskTolerance for point coloring
  const riskTolerance: IRiskTolerance | undefined = useMemo(() => {
    if (!exposureThresholds) return undefined;
    return {
      dangerMinus: exposureThresholds.dangerLow,
      warnMinus: exposureThresholds.warnLow,
      warnPlus: exposureThresholds.warnHigh,
      dangerPlus: exposureThresholds.dangerHigh,
    };
  }, [exposureThresholds]);

  const chartData = useMemo(() => {
    const firstDate = filteredData.length > 0 ? new Date(filteredData[0].createDate) : new Date();
    const lastDate = filteredData.length > 0 ? new Date(filteredData[filteredData.length - 1].createDate) : new Date();

    const thresholdDatasets = thresholdLines.map(({ skipLegend, color, value, label }, index) => ({
      label,
      data: [
        { x: firstDate, y: value },
        { x: lastDate, y: value },
      ],
      borderColor: color,
      borderWidth: 1.5,
      borderDash: [4, 6],
      pointRadius: 0,
      pointHoverRadius: 0,
      fill: false,
      skipLegend,
      tension: 0,
      order: index + 1,
    }));

    const calcExposure = (d: IExposureHistory) => d.futureOrders - d.inventory - d.financialPosition;
    const mult = isQty ? 1 : price;

    // Compute value-based color for the selected point using threshold zones
    const getPointColor = (i: number): string => {
      if (i !== selectedIndex || !riskTolerance) return EXPOSURE_COLOR;
      const val = calcExposure(filteredData[i]) * price; // always compare in $ for threshold check
      return getExposureColor(getExposureStatus(riskTolerance, val));
    };

    const pointBg = filteredData.map((_, i) => i === selectedIndex ? getPointColor(i) : '#FFFFFF');
    const pointBorder = filteredData.map((_, i) => getPointColor(i));
    const pointRadii = filteredData.map((_, i) => i === selectedIndex ? 7 : (filteredData.length > 30 ? 0 : 3));

    const exposureDataset = {
      label: 'Net Exposure',
      data: filteredData.map((d) => ({
        x: new Date(d.createDate),
        y: calcExposure(d) * mult,
      })),
      borderColor: EXPOSURE_COLOR,
      backgroundColor: EXPOSURE_COLOR + '20',
      borderWidth: 2.5,
      pointBackgroundColor: pointBg,
      pointBorderColor: pointBorder,
      pointBorderWidth: filteredData.map((_, i) => i === selectedIndex ? 2.5 : 1.5),
      pointRadius: pointRadii,
      pointHoverRadius: 6,
      tension: 0.3,
      fill: true,
      order: 0,
    };

    return { datasets: [...thresholdDatasets, exposureDataset] };
  }, [filteredData, thresholdLines, price, isQty, selectedIndex, riskTolerance]);

  const yPrefix = isQty ? '' : '$ ';
  const yAxisLabel = isQty ? 'Quantity (MT)' : 'Base currency ($)';

  const handleClick = useCallback((_event: ChartEvent, elements: ActiveElement[]) => {
    if (!onPointClick || !elements.length) return;
    const el = elements[0];
    // Only react to clicks on the exposure dataset (last one)
    const exposureDatasetIdx = chartData.datasets.length - 1;
    if (el.datasetIndex === exposureDatasetIdx && filteredData[el.index]) {
      // Toggle: clicking same point again deselects (returns to Latest)
      const clicked = filteredData[el.index];
      if (selectedSnapshot && clicked.createDate === selectedSnapshot.createDate) {
        onPointClick(null);
      } else {
        onPointClick(clicked);
      }
    }
  }, [onPointClick, chartData.datasets.length, filteredData, selectedSnapshot]);

  const chartOptions = useMemo<ChartOptions<'line'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      onClick: handleClick as any,
      scales: {
        x: {
          type: 'time',
          time: { unit: 'month', tooltipFormat: 'MMM dd, yyyy', displayFormats: { month: 'MMM yy' } },
          grid: { color: '#F1F5F9' },
          ticks: { color: '#475569', maxRotation: 0, autoSkipPadding: 16 },
        },
        y: {
          ticks: {
            color: '#475569',
            callback: (value) => FormatToShortPriceTemplate(+value, yPrefix, 0),
          },
          title: {
            display: true,
            text: yAxisLabel,
            color: '#0F172A',
            font: { size: 12, weight: 600 },
          },
          grid: { color: '#E2E8F0' },
        },
      },
      plugins: {
        legend: {
          position: 'bottom' as const,
          labels: {
            usePointStyle: true,
            pointStyle: 'line',
            pointStyleWidth: 24,
            filter: (item, data) => {
              const ds: any = data?.datasets[item?.datasetIndex || 0];
              return !ds?.skipLegend;
            },
          },
        },
        tooltip: {
          intersect: false,
          mode: 'index',
          filter: ({ dataset }: TooltipItem<'line'>) =>
            (dataset.label || '').startsWith('Net Exposure'),
          callbacks: {
            label: (context) => {
              const label = context.dataset.label || '';
              const value = context.parsed.y;
              const suffix = isQty ? ' MT' : '';
              return `${label}: ${FormatToShortPriceTemplate(value ?? 0, yPrefix, 1)}${suffix}`;
            },
          },
        },
      },
      elements: {
        line: { tension: 0.3 },
        point: { radius: 3, hoverRadius: 5 },
      },
    }),
    [formulaId, isGeneral, yAxisLabel, yPrefix, isQty, handleClick],
  );

  const materialLabel = useMemo(
    () => (isGeneral ? 'General' : materialsMap[formulaId]?.name || 'Selected material'),
    [formulaId, materialsMap, isGeneral],
  );

  const rangeLabel = useMemo(() => {
    if (!filteredData.length) return '';
    const firstYear = new Date(filteredData[0].createDate).getFullYear();
    const lastYear = new Date(filteredData[filteredData.length - 1].createDate).getFullYear();
    return firstYear === lastYear ? `${firstYear}` : `${firstYear} - ${lastYear}`;
  }, [filteredData]);

  return (
    <div className="flex flex-1 flex-col bg-white p-6 shadow-md rounded-xl h-full">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500">{materialLabel} - Historical exposure</p>
        </div>
        <div className="flex items-center gap-3">
          {!isGeneral && (
            <div className="flex items-center gap-1 bg-gray-100 rounded-full p-1">
              <button
                onClick={() => setIsQuantity(false)}
                className={`px-3 py-1.5 rounded-full transition-colors flex items-center gap-1.5 ${
                  !isQuantity
                    ? 'bg-white text-gray-900 shadow-sm font-medium'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <AttachMoney className="w-4 h-4" />
                {!isQuantity && <span className="text-xs">Value</span>}
              </button>
              <button
                onClick={() => setIsQuantity(true)}
                className={`px-3 py-1.5 rounded-full transition-colors flex items-center gap-1.5 ${
                  isQuantity
                    ? 'bg-white text-gray-900 shadow-sm font-medium'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Inventory className="w-4 h-4" />
                {isQuantity && <span className="text-xs">Qty</span>}
              </button>
            </div>
          )}
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
            {rangeLabel}
          </span>
          <ToggleChips
            chips={RANGE_OPTIONS.map((option, index) => ({ key: option.label, value: index }))}
            selected={selectedRangeIndex}
            setSelected={setSelectedRangeIndex}
            variant="filled"
            width="5rem"
          />
        </div>
      </div>
      <div className="mt-4 flex flex-1 flex-col overflow-hidden">
        {filteredData.length === 0 ? (
          <div className="flex flex-1 items-center justify-center text-sm text-gray-400">
            No exposure history data available
          </div>
        ) : (
          <div className="relative flex-1 min-h-[220px]">
            <Line
              ref={chartRef}
              data={chartData}
              options={chartOptions}
              redraw={false}
              className="absolute inset-0"
              style={{ height: '100%', width: '100%' }}
            />
          </div>
        )}
      </div>
    </div>
  );
};
